import React from 'react';
import { Link } from 'react-router-dom';
import { Check, ShoppingCart, Star } from 'lucide-react';
import { products } from '../../data/mock';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

const ProductsSection = () => {
  return (
    <section className="py-24 bg-gradient-to-b from-blue-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1.5 bg-orange-100 text-orange-700 text-sm font-semibold rounded-full mb-4">
            STEAM-IE Kits
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Premium Robotics & Coding Kits
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Sign up today and get a coding kit delivered at your doorstep that introduces 
            your child to the exciting world of coding and robotics.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 px-4 lg:px-0">
          {products.map((product) => (
            <div
              key={product.id}
              className="group bg-white rounded-2xl lg:rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100"
            >
              {/* Image */}
              <div className="relative h-48 lg:h-56 overflow-hidden">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                
                {/* Badge */}
                <Badge className={`absolute top-4 right-4 ${
                  product.badge === 'Bestseller' 
                    ? 'bg-green-500' 
                    : product.badge === '50% OFF'
                    ? 'bg-red-500'
                    : 'bg-blue-500'
                } text-white px-3 py-1`}>
                  {product.badge}
                </Badge>

                {/* Grade Badge */}
                <div className="absolute bottom-4 left-4">
                  <span className="bg-white/90 backdrop-blur-sm text-gray-800 px-3 py-1 rounded-full text-sm font-medium">
                    {product.grade}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                  {product.name}
                </h3>
                <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                  {product.description}
                </p>

                {/* Features */}
                <ul className="space-y-2 mb-6">
                  {product.features.slice(0, 3).map((feature, idx) => (
                    <li key={idx} className="flex items-center text-sm text-gray-600">
                      <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>

                {/* Price */}
                <div className="mb-4">
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className="text-2xl font-bold text-gray-900">
                      ₹{product.price.toLocaleString()}
                    </span>
                    {product.gst && (
                      <span className="text-xs text-gray-500">+ {product.gst}% GST</span>
                    )}
                  </div>
                  {product.priceWithGst && (
                    <p className="text-xs text-green-600 font-medium">
                      Total: ₹{product.priceWithGst.toLocaleString()} (incl. GST)
                    </p>
                  )}
                  {product.originalPrice && (
                    <span className="text-gray-400 line-through text-sm">
                      ₹{product.originalPrice.toLocaleString()}
                    </span>
                  )}
                  <div className="flex items-center text-yellow-500 mt-2">
                    <Star className="w-4 h-4 fill-current" />
                    <Star className="w-4 h-4 fill-current" />
                    <Star className="w-4 h-4 fill-current" />
                    <Star className="w-4 h-4 fill-current" />
                    <Star className="w-4 h-4 fill-current" />
                  </div>
                </div>

                {/* CTA */}
                <Link to="/products">
                  <Button className="w-full bg-gradient-to-r from-blue-600 to-teal-500 hover:from-blue-700 hover:to-teal-600 text-white rounded-xl py-3 group/btn">
                    <ShoppingCart className="w-4 h-4 mr-2 group-hover/btn:scale-110 transition-transform" />
                    Buy Now
                  </Button>
                </Link>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <Link to="/products">
            <Button variant="outline" className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50 px-8 py-6 rounded-full text-lg">
              View All Products
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ProductsSection;
